import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Label } from 'ui';
import { useAuth } from 'hooks/useAuth';
import {
  StyledBlock,
  StyledSiderHeader,
  CustomMenu,
  StyledMenuSider,
} from './style';

import { menuItems, menuItemsAdmin } from 'config/menuItems';
import SidebarMenuItem from './SidebarMenuItem/SidebarMenuItem';
import useQueryApiClient from 'utils/useQueryApiClient';
import { useRealtimeFarmNotifications } from 'realtime/useRealtimeFarmNotifications';

const Sider = () => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { user, can } = useAuth();

  const isAdmin = useMemo(() => user?.isAdmin, [user?.isAdmin]);
  const farmId = user?.farmId ?? user?.farm?.id;

  const { data: unreadData, refetch: refetchUnread } = useQueryApiClient({
    request: {
      url: '/api/v2/notifications/unread-count',
      data: { farm_id: farmId },
    },
  });

  const unreadCount =
    typeof unreadData?.data?.count === 'number'
      ? unreadData.data.count
      : typeof unreadData?.count === 'number'
      ? unreadData.count
      : 0;

  useEffect(() => {
    if (farmId) {
      refetchUnread();
    }
  }, [farmId]);

  const handleRealtimeEvent = useCallback(() => {
    refetchUnread();
  }, [refetchUnread]);

  useRealtimeFarmNotifications({
    farmId,
    eventName: '.farm.notification',
    onEvent: handleRealtimeEvent,
  });

  useRealtimeFarmNotifications({
    farmId,
    eventName: '.notification.updated',
    onEvent: handleRealtimeEvent,
  });

  const SiderContent = () => (
    <>
      <StyledSiderHeader>
        <img src={'/logo_ng.png'} width={69} height={66} alt={'logo'} />
        <Label
          color={'white'}
          label={intl.formatMessage({ id: 'general.project_name' })}
        />
      </StyledSiderHeader>

      <StyledBlock paddingTop={23} borderBottom={true}>
        <div className="role">{user?.farmName}</div>
        <CustomMenu>
          {menuItems.map((item, index) => {
            if (item?.ability && !can(item.ability)) {
              return <></>;
            }

            if (
              item.requiresWholesaleContract &&
              !user?.isAdmin &&
              !(user?.farm?.has_wholesale_contract ?? false)
            ) {
              return <></>;
            }

            return (
              <SidebarMenuItem
                key={index}
                labelId={intl.formatMessage({ id: item.labelId })}
                url={item.url}
                icon={item.icon}
                badgeCount={item.badgeCount}
                active={
                  pathname === item.url || pathname.startsWith(`${item.url}/`)
                }
              />
            );
          })}
        </CustomMenu>
      </StyledBlock>

      <StyledBlock paddingTop={60} borderBottom={false}>
        <CustomMenu>
          {menuItemsAdmin
            .filter((item) => item.adminOnly === false || isAdmin)
            .map((item, index) => (
              <SidebarMenuItem
                key={index + 100}
                labelId={intl.formatMessage({ id: item.labelId })}
                url={item.url}
                icon={item.icon}
                badgeCount={
                  item.labelId === 'navigation.notifications'
                    ? unreadCount
                    : item.badgeCount
                }
                active={
                  pathname === item.url || pathname.startsWith(`${item.url}/`)
                }
              />
            ))}
        </CustomMenu>
      </StyledBlock>
    </>
  );

  return (
    <StyledMenuSider className="menu-sider" width={346}>
      <SiderContent />
    </StyledMenuSider>
  );
};

export default Sider;
